import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  RegionDto,
  SupplierDto,
  SupplierRegionDto,
  SupplierRepresentativeDto,
  UserDto,
} from '@vatos-pas/common';
import { extractErrorMessage } from 'utils';

/**
 * Adapter between the data layer and supplier components — components stay
 * data-layer agnostic so the underlying client (react-admin, react-query,
 * Apollo, fetch) can be swapped without touching them.
 *
 * `useGetOne` provides SWR semantics via the react-admin Redux cache: cached
 * data returns immediately, refetch happens in the background, and the loading
 * spinner only shows on first load when no cached data exists.
 */

export interface RegionOption {
  id: string;
  name: string;
}

export interface UserRepresentativeOption {
  id: string;
  name: string;
}

export interface SupplierFormValues {
  name: string;
  externalId: string;
  description: string;
  email: string;
  phone: string;
  contactInfo: string;
  selectedRegions: string[];
  selectedRepresentatives: string[];
}

function useSupplier() {
  const dataProvider = useDataProvider();

  const [supplierId, setSupplierId] = useState<string>('');
  const {
    data: selectedSupplier,
    loading: supplierLoading,
    loaded: supplierLoaded,
    error: supplierError,
  } = useGetOne<SupplierDto>('supplier', supplierId, {
    enabled: !!supplierId,
  });

  const isLoading = !!supplierId && !selectedSupplier && supplierLoading;
  const isRevalidating = !!supplierId && supplierLoading;

  const [formLoading, setFormLoading] = useState(false);
  const [supplierRegions, setSupplierRegions] = useState<SupplierRegionDto[]>(
    [],
  );
  const [supplierRepresentatives, setSupplierRepresentatives] = useState<
    SupplierRepresentativeDto[]
  >([]);
  const [supplierRegionsLoaded, setSupplierRegionsLoaded] = useState(false);
  const [supplierRepresentativesLoaded, setSupplierRepresentativesLoaded] =
    useState(false);
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const [representativesError, setRepresentativesError] = useState<
    string | null
  >(null);

  const loadSupplierById = useCallback((id: string): void => {
    setSupplierId(id);
  }, []);

  const createSupplier = useCallback(
    async (
      data: Omit<
        SupplierFormValues,
        'selectedRegions' | 'selectedRepresentatives'
      >,
    ): Promise<SupplierDto> => {
      const result = await dataProvider.create<SupplierDto>('supplier', {
        data,
      });
      const supplier = result.data;
      setSupplierId(supplier.id);
      return supplier;
    },
    [dataProvider],
  );

  const updateSupplier = useCallback(
    async (
      id: string,
      data: Partial<
        Omit<SupplierFormValues, 'selectedRegions' | 'selectedRepresentatives'>
      >,
    ): Promise<SupplierDto> => {
      if (!selectedSupplier) {
        throw new Error('Cannot update: supplier data not loaded');
      }
      const result = await dataProvider.update<SupplierDto>('supplier', {
        id,
        data: { ...selectedSupplier, ...data },
        previousData: selectedSupplier,
      });
      return result.data;
    },
    [dataProvider, selectedSupplier],
  );

  const loadRegionOptions = useCallback(
    async (_searchTerm: string): Promise<RegionOption[]> => {
      try {
        const result = await dataProvider.getList<RegionDto>('region', {
          filter: {},
          pagination: { page: 1, perPage: 999 },
          sort: { field: 'name', order: 'ASC' },
        });
        setRegionsError(null);
        return result.data.map(region => ({
          id: region.id,
          name: region.name,
        }));
      } catch (error) {
        console.error('Failed to load regions:', error);
        setRegionsError(extractErrorMessage(error, 'Failed to load regions'));
        return [];
      }
    },
    [dataProvider],
  );

  const loadUserRepresentativeOptions = useCallback(
    async (searchTerm: string): Promise<UserRepresentativeOption[]> => {
      try {
        const result = await dataProvider.getList<UserDto>(
          'user-representative',
          {
            filter: searchTerm
              ? {
                  s: {
                    $or: [
                      { firstName: { $cont: searchTerm } },
                      { lastName: { $cont: searchTerm } },
                    ],
                  },
                }
              : {},
            pagination: { page: 1, perPage: 80 },
            sort: { field: 'firstName', order: 'ASC' },
          },
        );
        setRepresentativesError(null);
        return result.data.map(user => ({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
        }));
      } catch (error) {
        console.error('Failed to load user representatives:', error);
        setRepresentativesError(
          extractErrorMessage(error, 'Failed to load supplier representatives'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  const loadSupplierRegionsForSupplier = useCallback(
    async (supplierId: string): Promise<SupplierRegionDto[]> => {
      try {
        const result = await dataProvider.getList<SupplierRegionDto>(
          'supplier-region',
          {
            filter: { supplierId },
            pagination: { page: 1, perPage: 80 },
            sort: { field: 'id', order: 'DESC' },
          },
        );
        setSupplierRegions(result.data);
        setSupplierRegionsLoaded(true);
        setRegionsError(null);
        return result.data;
      } catch (error) {
        console.error('Failed to load supplier regions:', error);
        setSupplierRegions([]);
        setSupplierRegionsLoaded(true);
        setRegionsError(
          extractErrorMessage(error, 'Failed to load supplier regions'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  const loadSupplierRepresentativesForSupplier = useCallback(
    async (supplierId: string): Promise<SupplierRepresentativeDto[]> => {
      try {
        const result = await dataProvider.getList<SupplierRepresentativeDto>(
          'supplier-representative',
          {
            filter: { supplierId },
            pagination: { page: 1, perPage: 80 },
            sort: { field: 'id', order: 'DESC' },
          },
        );
        setSupplierRepresentatives(result.data);
        setSupplierRepresentativesLoaded(true);
        setRepresentativesError(null);
        return result.data;
      } catch (error) {
        console.error('Failed to load supplier representatives:', error);
        setSupplierRepresentatives([]);
        setSupplierRepresentativesLoaded(true);
        setRepresentativesError(
          extractErrorMessage(error, 'Failed to load supplier representatives'),
        );
        return [];
      }
    },
    [dataProvider],
  );

  const createSupplierRegion = useCallback(
    async (supplierId: string, regionId: string): Promise<void> => {
      await dataProvider.create('supplier-region', {
        data: { supplierId, regionId },
      });
    },
    [dataProvider],
  );

  const deleteSupplierRegion = useCallback(
    async (supplierRegionId: string): Promise<void> => {
      await dataProvider.delete('supplier-region', {
        id: supplierRegionId,
        previousData: { id: supplierRegionId },
      });
    },
    [dataProvider],
  );

  const createSupplierRepresentative = useCallback(
    async (supplierId: string, userId: string): Promise<void> => {
      await dataProvider.create('supplier-representative', {
        data: { supplierId, userId },
      });
    },
    [dataProvider],
  );

  const deleteSupplierRepresentative = useCallback(
    async (supplierRepresentativeId: string): Promise<void> => {
      await dataProvider.delete('supplier-representative', {
        id: supplierRepresentativeId,
        previousData: { id: supplierRepresentativeId },
      });
    },
    [dataProvider],
  );

  const batchUpdateSupplierRegions = useCallback(
    async (
      supplierId: string,
      existingSupplierRegions: SupplierRegionDto[],
      newRegionIds: string[],
    ): Promise<void> => {
      const existingRegionIds = existingSupplierRegions.map(sr => sr.regionId);
      const regionsToAdd = newRegionIds.filter(
        id => !existingRegionIds.includes(id),
      );
      const regionsToDelete = existingSupplierRegions.filter(
        sr => !newRegionIds.includes(sr.regionId),
      );

      // Additions first: the API refuses a Regional Admin removing a Supplier's last Region.
      const addPromises = regionsToAdd.map(regionId =>
        createSupplierRegion(supplierId, regionId),
      );
      await Promise.all(addPromises);

      const deletePromises = regionsToDelete.map(sr =>
        deleteSupplierRegion(sr.id),
      );
      await Promise.all(deletePromises);
    },
    [createSupplierRegion, deleteSupplierRegion],
  );

  const batchUpdateSupplierRepresentatives = useCallback(
    async (
      supplierId: string,
      existingSupplierRepresentatives: SupplierRepresentativeDto[],
      newUserIds: string[],
    ): Promise<void> => {
      const existingUserIds = existingSupplierRepresentatives.map(
        sr => sr.userId,
      );
      const usersToAdd = newUserIds.filter(
        id => !existingUserIds.includes(id),
      );
      const usersToDelete = existingSupplierRepresentatives.filter(
        sr => !newUserIds.includes(sr.userId),
      );

      const deletePromises = usersToDelete.map(sr =>
        deleteSupplierRepresentative(sr.id),
      );
      await Promise.all(deletePromises);

      const addPromises = usersToAdd.map(userId =>
        createSupplierRepresentative(supplierId, userId),
      );
      await Promise.all(addPromises);
    },
    [createSupplierRepresentative, deleteSupplierRepresentative],
  );

  const resetLoadedFlags = useCallback(() => {
    setSupplierRegionsLoaded(false);
    setSupplierRepresentativesLoaded(false);
  }, []);

  return {
    selectedSupplier,
    isLoading,
    isRevalidating,
    supplierLoaded,
    supplierError,
    formLoading,
    supplierRegions,
    supplierRepresentatives,
    supplierRegionsLoaded,
    supplierRepresentativesLoaded,
    regionsError,
    representativesError,
    loadSupplierById,
    createSupplier,
    updateSupplier,
    loadRegionOptions,
    loadUserRepresentativeOptions,
    loadSupplierRegionsForSupplier,
    loadSupplierRepresentativesForSupplier,
    batchUpdateSupplierRegions,
    batchUpdateSupplierRepresentatives,
    setFormLoading,
    resetLoadedFlags,
  };
}

export const SupplierContainer = createContainer(useSupplier);
